import type { SubscriptionStore } from "@/Modules/Subscription/Store";
import type { AppStore } from "@/Modules/App/Store";
import type { SubscribeUiQuery } from "@/Modules/Models/Domain/Subscription";
import type { ApiError } from "@/Services/Api/Models";
import type { SubscribeApiQuery, SubscriptionApi } from "@/Modules/Models/Api/Subscription";
import type { Subscription } from "@/Modules/Models/Domain/Subscription";

import SubscriptionApiConnector from "@/Services/TherapyApi/Subscriptions";

import useAppStore from "@/Modules/App/Store";

import { ApiErrorCodes } from "@/Modules/App/Models/Api/ApiErrorCodes";
import { NotificationType } from "@/Modules/App/Models/Domain/NotificationType";
import camelize from "@/Utils/Camelize";
import { isValidString } from "@/Utils/String";

class SubscribeToPlanActionHandler {
  constructor(
    private readonly subscriptionStore: SubscriptionStore,
    private readonly appStore: AppStore = useAppStore(),
    private readonly subscriptionApiConnector: SubscriptionApiConnector = new SubscriptionApiConnector()
  ) {}

  public async handle(
    subscribeUiQuery: SubscribeUiQuery,
    isExtra: boolean = false
  ): Promise<Subscription | void> {
    this.subscriptionStore.isLoading = true;

    const subscriptionApi = this.subscriptionDomainToApi(subscribeUiQuery);

    return this.subscriptionApiConnector
      .subscribeToPlan(subscriptionApi)
      .then((subscriptionResponse) => {
        const subscription = this.subscriptionApiToDomain(subscriptionResponse);

        this.subscriptionStore.subscription = subscription;

        if (isExtra) {
          this.appStore.showSnackbar({
            type: NotificationType.SUCCESS,
            message: "user.settings.subscription.sessions_extra.success.message",
          });
        }

        return subscription;
      })
      .catch((error: ApiError) => {
        let message: string;

        if (
          error.error_code === ApiErrorCodes.CHANGE_SUBSCRIPTION_ERROR ||
          error.error_code === ApiErrorCodes.CREATE_SUBSCRIPTION_ERROR
        ) {
          message = "user.settings.subscription.payment.card_error.generic_decline";
        } else {
          message = "there_was_an_error_connecting_to_the_server_please_try_again_later";
        }

        if (isValidString(message) && isExtra) {
          message = "user.settings.subscription.sessions_extra.error.message";
        }

        this.appStore.showSnackbar({
          type: NotificationType.ERROR,
          message: message,
        });

        this.subscriptionStore.notification = {
          type: NotificationType.ERROR,
          code: error.error_code,
          message,
        };

        return undefined;
      })
      .finally(() => {
        this.subscriptionStore.isLoading = false;
      });
  }

  private subscriptionApiToDomain(subscriptionResponse: SubscriptionApi): Subscription {
    return camelize<SubscriptionApi>(subscriptionResponse) as Subscription;
  }

  private subscriptionDomainToApi(subscriptionUiQuery: SubscribeUiQuery): SubscribeApiQuery {
    if (subscriptionUiQuery.promoPlanId) {
      return {
        plan_uuid: subscriptionUiQuery.planId,
        user_uuid: subscriptionUiQuery.userId,
        promo_plan_uuid: subscriptionUiQuery.promoPlanId,
      };
    }
    return {
      plan_uuid: subscriptionUiQuery.planId,
      user_uuid: subscriptionUiQuery.userId,
    };
  }
}

export default SubscribeToPlanActionHandler;
